import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiUsers, FiAward, FiTrendingUp, FiEye, FiFilter, FiStar } = FiIcons;

const Home = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [isScrolled, setIsScrolled] = useState(false);

  // Add scroll listener to detect when user scrolls
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      title: 'Branding & Identity',
      description: 'Create memorable brand identities that resonate with your target audience and stand out in the market.',
      icon: FiAward,
      link: '/services/branding'
    },
    {
      title: 'Web Design & Development',
      description: 'Build responsive, user-friendly websites that convert visitors into customers.',
      icon: FiTrendingUp,
      link: '/services/web-design'
    },
    {
      title: 'Print Design',
      description: 'Professional print materials that make a lasting impression on your clients.',
      icon: FiEye,
      link: '/services/print-design'
    },
    {
      title: 'Digital Marketing',
      description: 'Strategic digital campaigns that drive engagement and grow your business.',
      icon: FiUsers,
      link: '/services/digital-marketing'
    }
  ];

  const portfolioItems = [
    {
      title: 'Spice Route FC Restaurant',
      category: 'Branding',
      image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&h=400&fit=crop'
    },
    {
      title: 'Karoo Wind Energy Solutions',
      category: 'Web Design',
      image: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=600&h=400&fit=crop'
    },
    {
      title: 'Ubuntu Foundation',
      category: 'Digital Marketing',
      image: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=600&h=400&fit=crop'
    },
    {
      title: 'Stellenbosch Wine Estate',
      category: 'Print Design',
      image: 'https://media.phst.co.za/file/ph/creativesol-portfolio-wine.jpg'
    },
    {
      title: 'TechHub Johannesburg',
      category: 'Branding',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=400&fit=crop'
    },
    {
      title: 'Mama Afrika Fashion',
      category: 'Web Design',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop'
    }
  ];

  const testimonials = [
    {
      text: "Creative Solutions Agency transformed our brand completely. The new identity perfectly captures our vision and has significantly improved our market presence.",
      author: "Sarah Johnson",
      company: "Spice Route FC",
      rating: 5
    },
    {
      text: "The team's attention to detail and creative approach exceeded our expectations. Our website now converts 40% better than before.",
      author: "Michael Chen",
      company: "Karoo Solar Solutions",
      rating: 5
    },
    {
      text: "Professional, creative, and results-driven. They helped us secure major funding through exceptional brand design.",
      author: "Thabo Mthembu",
      company: "TechHub Johannesburg",
      rating: 5
    }
  ];

  const filters = ['All', 'Branding', 'Web Design', 'Print Design', 'Digital Marketing'];
  const filteredPortfolio = activeFilter === 'All' ? portfolioItems : portfolioItems.filter(item => item.category === activeFilter);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white"
            >
              <h1 className="font-poppins font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-6 text-white">
                Redefine Your Brand with{' '}
                <span className="text-purple-300">Creative Excellence</span>
              </h1>
              <p className="font-inter text-lg md:text-xl text-white mb-8 leading-relaxed">
                We craft compelling visual identities and digital experiences that make your business unforgettable in the South African market and beyond.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/portfolio"
                  className="inline-flex items-center justify-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105"
                >
                  View Our Work <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-poppins font-semibold rounded-lg hover:bg-white hover:text-teal-600 transition-all duration-300"
                >
                  Start Your Project
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="w-96 h-96 bg-white/10 rounded-3xl backdrop-blur-sm border border-white/20 p-8">
                  <div className="w-full h-full bg-gradient-to-br from-purple-600/20 to-teal-600/20 rounded-2xl flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <span className="text-teal-600 font-poppins font-bold text-2xl">CSA</span>
                      </div>
                      <p className="text-white font-inter text-lg">Bringing Ideas to Life</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              From brand identity to digital marketing, we offer comprehensive creative solutions to elevate your business.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-lg card-hover border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-teal-600 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-poppins font-semibold text-xl text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="font-inter text-gray-600 mb-4 leading-relaxed">
                  {service.description}
                </p>
                <Link
                  to={service.link}
                  className="inline-flex items-center text-purple-600 font-inter font-medium hover:text-teal-600 transition-colors duration-200"
                >
                  Learn More <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Work */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              Featured Work
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Explore our portfolio of successful projects that have transformed businesses across South Africa.
            </p>
            {/* Filter Buttons */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {filters.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-6 py-2 rounded-full font-inter font-medium transition-all duration-300 ${activeFilter === filter
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-purple-600 hover:text-white'
                    }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPortfolio.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden rounded-xl card-hover"
              >
                <div className="aspect-w-16 aspect-h-12">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <span className="inline-block px-3 py-1 bg-purple-600 rounded-full text-sm font-inter font-medium mb-2">
                      {item.category}
                    </span>
                    <h3 className="font-poppins font-semibold text-lg">{item.title}</h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/portfolio"
              className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105"
            >
              View All Projects <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              Why Choose Us
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              With over a decade of experience, we've helped hundreds of businesses achieve their creative goals.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                number: '10+',
                label: 'Years Experience',
                desc: 'Delivering creative excellence since 2013'
              },
              {
                number: '100+',
                label: 'Happy Clients',
                desc: 'Successful projects across South Africa'
              },
              {
                number: '5+',
                label: 'Award Winning',
                desc: 'Recognized for outstanding design work'
              }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-24 h-24 bg-gradient-to-br from-purple-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-poppins font-bold text-2xl text-white">{stat.number}</span>
                </div>
                <h3 className="font-poppins font-semibold text-xl text-gray-900 mb-2">
                  {stat.label}
                </h3>
                <p className="font-inter text-gray-600">{stat.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4">
              What Our Clients Say
            </h2>
            <p className="font-inter text-lg text-gray-100 max-w-2xl mx-auto">
              Don't just take our word for it. Here's what our clients have to say about working with us.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.author}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="font-inter text-gray-600 mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div>
                  <h4 className="font-poppins font-semibold text-gray-900">
                    {testimonial.author}
                  </h4>
                  <p className="font-inter text-sm text-gray-500">{testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4">
              Ready to Elevate Your Brand?
            </h2>
            <p className="font-inter text-lg text-gray-300 max-w-2xl mx-auto mb-8">
              Let's discuss your project and create something amazing together. Get in touch for a free consultation.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105"
            >
              Start Your Project Today <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;